import { useCallback, useEffect, useRef, useState } from 'react'
import { Bold, Heading2, Italic, Link2, List, ListOrdered, Quote, Undo2, User } from 'lucide-react'
import { sanitizeEmailHtml } from '../../../lib/emailHtml'

// Formatting toolbar over a contenteditable, rather than a raw HTML textarea.
//
// No editor dependency: the requirement is a handful of formatting commands
// whose output has to be email-safe, and every general-purpose editor emits
// markup (nested <span style>, <div> soup) that Outlook renders badly. What
// the author types goes through sanitizeEmailHtml, which rebuilds it from a
// small allowlist with inline styles.
//
// The content is normalised on blur, not on every keystroke — rewriting the
// DOM under a live caret makes it jump to the start of the field.

const COMMANDS = [
  { id: 'bold', label: 'Bold', icon: Bold, exec: () => document.execCommand('bold') },
  { id: 'italic', label: 'Italic', icon: Italic, exec: () => document.execCommand('italic') },
  { id: 'h2', label: 'Heading', icon: Heading2, exec: () => document.execCommand('formatBlock', false, 'h2') },
  { id: 'ul', label: 'Bulleted list', icon: List, exec: () => document.execCommand('insertUnorderedList') },
  { id: 'ol', label: 'Numbered list', icon: ListOrdered, exec: () => document.execCommand('insertOrderedList') },
  { id: 'quote', label: 'Quote', icon: Quote, exec: () => document.execCommand('formatBlock', false, 'blockquote') },
]

// True when `html` would render to exactly the nodes `el` already holds.
// Compared after a parse round-trip, not as raw strings: the browser's
// serialiser can spell the same DOM differently from the string that built
// it, and treating that as a change rewrites the nodes — which drops the
// caret and any Range we saved for the link prompt.
function sameMarkup(html, el) {
  if (html === el.innerHTML) return true
  const probe = document.createElement('div')
  probe.innerHTML = html
  return probe.innerHTML === el.innerHTML
}

// The <a> around the caret, if the selection sits inside one in this editor.
function anchorAtSelection(el) {
  const selection = window.getSelection()
  const node = selection?.rangeCount ? selection.getRangeAt(0).startContainer : null
  if (!node) return null
  const from = node.nodeType === 1 ? node : node.parentElement
  const anchor = from?.closest('a')
  return anchor && el.contains(anchor) ? anchor : null
}

export default function RichTextEditor({ value, onChange, disabled = false, ariaLabel = 'Campaign body' }) {
  const ref = useRef(null)
  const rootRef = useRef(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const savedRange = useRef(null)
  // The <a> being edited, when the prompt opened with the caret inside one.
  const [editingAnchor, setEditingAnchor] = useState(null)

  // Only write into the DOM when the incoming value genuinely differs from
  // what's rendered — otherwise every parent re-render resets the caret.
  useEffect(() => {
    const el = ref.current
    if (el && !sameMarkup(value || '', el)) el.innerHTML = value || ''
  }, [value])

  const emit = useCallback(() => {
    if (ref.current) onChange(ref.current.innerHTML)
  }, [onChange])

  // Normalise once the author looks away, so the stored HTML is always the
  // email-safe form even if they never press Save.
  const normalise = useCallback(() => {
    const el = ref.current
    if (!el) return
    const clean = sanitizeEmailHtml(el.innerHTML)
    if (!sameMarkup(clean, el)) el.innerHTML = clean
    onChange(clean)
  }, [onChange])

  // Focus moving to the link prompt is not "looking away": normalising then
  // could rewrite the nodes the saved selection points at, and the link would
  // land nowhere. The save path sanitises anyway, so nothing is lost.
  const onBlur = (e) => {
    if (rootRef.current?.contains(e.relatedTarget)) return
    normalise()
  }

  const run = (command) => {
    if (disabled) return
    ref.current?.focus()
    command.exec()
    emit()
  }

  // Paste as plain text. Pasting from a web page or Google Doc otherwise drags
  // in fonts, colours and layout that break the email template.
  const onPaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
    emit()
  }

  const openLink = () => {
    if (disabled) return
    const selection = window.getSelection()
    const anchor = anchorAtSelection(ref.current)
    setEditingAnchor(anchor)
    if (anchor) {
      // Caret inside an existing link: edit that link's URL rather than
      // wrapping a new <a> around whatever happened to be selected.
      const range = document.createRange()
      range.selectNodeContents(anchor)
      savedRange.current = range
      setLinkUrl(anchor.getAttribute('href') || '')
    } else {
      savedRange.current = selection?.rangeCount ? selection.getRangeAt(0).cloneRange() : null
      setLinkUrl('')
    }
    setLinkOpen(true)
  }

  const closeLink = () => {
    setLinkOpen(false)
    setLinkUrl('')
    setEditingAnchor(null)
  }

  const restoreSelection = () => {
    ref.current?.focus()
    // Restore what was selected before the URL field stole focus.
    if (savedRange.current) {
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(savedRange.current)
    }
  }

  const applyLink = () => {
    if (!ref.current) return
    const url = linkUrl.trim()
    restoreSelection()
    if (editingAnchor && ref.current.contains(editingAnchor)) {
      // Set the attribute directly: execCommand('createLink') over an existing
      // anchor nests or splits it depending on the browser.
      if (url) editingAnchor.setAttribute('href', url)
    } else if (url) {
      document.execCommand('createLink', false, url)
    }
    closeLink()
    emit()
  }

  const removeLink = () => {
    if (!ref.current || !editingAnchor || !ref.current.contains(editingAnchor)) return
    restoreSelection()
    editingAnchor.replaceWith(...editingAnchor.childNodes)
    closeLink()
    emit()
  }

  const insertMergeTag = () => {
    if (disabled) return
    ref.current?.focus()
    document.execCommand('insertText', false, '{{first_name}}')
    emit()
  }

  const btn = 'inline-flex h-8 w-8 items-center justify-center rounded hover:bg-muted disabled:opacity-40'

  return (
    <div ref={rootRef} className={`rounded-md border border-input ${disabled ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1">
        {COMMANDS.map((command) => (
          <button
            key={command.id}
            type="button"
            className={btn}
            title={command.label}
            aria-label={command.label}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()} /* keep the selection */
            onClick={() => run(command)}
          >
            <command.icon className="h-4 w-4" />
          </button>
        ))}

        <button type="button" className={btn} title="Add or edit link" aria-label="Add link"
          disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={openLink}>
          <Link2 className="h-4 w-4" />
        </button>

        <button type="button" className={btn} title="Remove formatting" aria-label="Remove formatting"
          disabled={disabled} onMouseDown={(e) => e.preventDefault()}
          onClick={() => run({ exec: () => document.execCommand('removeFormat') })}>
          <Undo2 className="h-4 w-4" />
        </button>

        <span className="mx-1 h-5 w-px bg-border" />

        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 rounded px-2 text-xs font-medium hover:bg-muted disabled:opacity-40"
          title="Insert the recipient's first name"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertMergeTag}
        >
          <User className="h-3.5 w-3.5" />
          First name
        </button>
      </div>

      {linkOpen && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 p-2">
          <input
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink() }
              if (e.key === 'Escape') closeLink()
            }}
            placeholder="https://getdub.com/…"
            aria-label="Link URL"
            className="h-8 flex-1 rounded border border-input bg-background px-2 text-sm"
          />
          <button type="button" onClick={applyLink} className="h-8 rounded bg-primary px-3 text-xs font-medium text-primary-foreground">
            {editingAnchor ? 'Update' : 'Add'}
          </button>
          {editingAnchor && (
            <button type="button" onClick={removeLink} className="h-8 rounded px-2 text-xs text-[var(--orange-11)]">
              Remove link
            </button>
          )}
          <button type="button" onClick={closeLink} className="h-8 rounded px-2 text-xs">
            Cancel
          </button>
        </div>
      )}

      <div
        ref={ref}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={onBlur}
        onPaste={onPaste}
        className="crm-editor min-h-[320px] overflow-y-auto p-3 text-sm outline-none"
      />
    </div>
  )
}
